import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { User } from '@/api/auth';
import type { GameV1 } from '@/api/games';
import { isLimitReached } from '@/helpers/limits';
import { Modals } from '@/helpers/modals';
import { usePlayerFields } from '@/modules/common/hooks/usePlayerFields';
import { useUserLimits } from '@/modules/common/hooks/useUserLimits';
import { useGetCommonStore, useModalsStore } from '@/store';
import { useNavigation } from './navigation';
import { usePlayerFieldsStatus } from './usePlayerFieldsStatus';

export enum PlayerFields {
  COUNTRY = 'country',
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  DATE_OF_BIRTH = 'dateOfBirth',
  GENDER = 'gender',
}

export const isPlayerFieldsFilled = (
  user: User,
  propertiesToCheck: (keyof User)[]
) => {
  const missingFields: (keyof User)[] = [];

  for (const property of propertiesToCheck) {
    if (!user[property]) {
      missingFields.push(property);
    }
  }

  return {
    isAllFieldsFilled: missingFields.length === 0,
    missingFields,
  };
};

export function useGameRedirect(gameProvider?: GameV1['provider']) {
  const useCommonStore = useGetCommonStore();

  const { isAuthenticated, user } = useCommonStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      user: state.user,
    }))
  );
  const open = useModalsStore.use.open();
  const [inProcessRedirect, setInProcessRedirect] = useState(true);

  const { navigate } = useNavigation();
  const { push } = useRouter();
  const pathname = usePathname();

  const { data: playerFields } = usePlayerFields();

  const { data: userLimitsData, isLoading: userLimitsPending } =
    useUserLimits();

  const { missingFields, isAllFieldsFilled } =
    usePlayerFieldsStatus(gameProvider);

  useEffect(() => {
    if (userLimitsPending || !userLimitsData) return;
    if (!(gameProvider && playerFields && isAuthenticated && user)) {
      setInProcessRedirect(false);
      return;
    }

    const isLimitedAccess = isLimitReached(userLimitsData, user.currency);

    if (isAllFieldsFilled && !isLimitedAccess) {
      setInProcessRedirect(false);
      return;
    }

    if (!isAllFieldsFilled || isLimitedAccess) {
      if (missingFields.length) {
        useCommonStore.setState({
          gameOpenBackUrl: window.location.href,
        });

        open(Modals.CompleteProfile);
        return;
      }

      const limitsLink = `limits${
        isLimitedAccess ? '?action=limitedAccess' : ''
      }`;
      const personalLink = `personal?back-url=${encodeURIComponent(pathname)}`;

      navigate(
        `/profile-custom/${isLimitedAccess ? limitsLink : personalLink}`,
        true
      );
      return;
    }

    setInProcessRedirect(false);
  }, [
    gameProvider,
    isAuthenticated,
    navigate,
    playerFields,
    user,
    pathname,
    userLimitsPending,
    userLimitsData,
    open,
    push,
    isAllFieldsFilled,
    missingFields.length,
  ]);

  return inProcessRedirect;
}
